import { jsPDF } from 'jspdf';

/**
 * Format currency values
 */
const formatCurrency = (amount) => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  })?.format(amount);
};

/**
 * Format date values
 */
const formatDate = (dateString) => {
  return new Date(dateString)?.toLocaleDateString('en-US', {
    month: '2-digit',
    day: '2-digit',
    year: 'numeric'
  });
};

/**
 * Format name based on privacy settings
 */
const formatNameByPrivacy = (name, displayMode) => {
  if (!name) return 'N/A';
  
  if (displayMode === 'hidden') {
    return '[Hidden]';
  }
  
  if (displayMode === 'initials') {
    const nameParts = name?.trim()?.split(/\s+/);
    if (nameParts?.length === 1) {
      return nameParts?.[0]?.charAt(0)?.toUpperCase() + '.';
    }
    return nameParts?.map(part => part?.charAt(0)?.toUpperCase() + '.')?.join(' ');
  }
  
  // displayMode === 'full'
  return name;
};

/**
 * Draw a table manually using jsPDF primitives
 */
const drawTable = (doc, headers, rows, startY, options = {}) => {
  const {
    startX = 20,
    columnWidths = [],
    headerColor = [37, 99, 235],
    headerTextColor = [255, 255, 255],
    rowHeight = 8,
    headerHeight = 10,
    fontSize = 8,
    headerFontSize = 9,
    alternateRowColor = [245, 245, 245],
    borderColor = [200, 200, 200]
  } = options;

  let currentY = startY;
  const pageWidth = doc?.internal?.pageSize?.width;
  const tableWidth = pageWidth - (startX * 2);
  
  // Calculate column widths if not provided
  const colWidths = columnWidths?.length > 0 
    ? columnWidths 
    : Array(headers?.length)?.fill(tableWidth / headers?.length);

  // Draw header
  doc?.setFillColor(...headerColor);
  doc?.rect(startX, currentY, tableWidth, headerHeight, 'F');
  
  doc?.setTextColor(...headerTextColor);
  doc?.setFontSize(headerFontSize);
  doc?.setFont(undefined, 'bold');
  
  let currentX = startX;
  headers?.forEach((header, i) => {
    doc?.text(header, currentX + 2, currentY + 6);
    currentX += colWidths?.[i];
  });
  
  currentY += headerHeight;

  // Draw rows
  doc?.setFont(undefined, 'normal');
  doc?.setFontSize(fontSize);
  doc?.setTextColor(50, 50, 50);
  
  rows?.forEach((row, rowIndex) => {
    // Check if we need a new page
    if (currentY > 270) {
      doc?.addPage();
      currentY = 20;
      
      // Redraw header on new page
      doc?.setFillColor(...headerColor);
      doc?.rect(startX, currentY, tableWidth, headerHeight, 'F');
      doc?.setTextColor(...headerTextColor);
      doc?.setFontSize(headerFontSize);
      doc?.setFont(undefined, 'bold');
      
      currentX = startX;
      headers?.forEach((header, i) => {
        doc?.text(header, currentX + 2, currentY + 6);
        currentX += colWidths?.[i];
      });
      
      currentY += headerHeight;
      doc?.setFont(undefined, 'normal');
      doc?.setFontSize(fontSize);
      doc?.setTextColor(50, 50, 50);
    }

    // Alternate row colors
    if (rowIndex % 2 === 1) {
      doc?.setFillColor(...alternateRowColor);
      doc?.rect(startX, currentY, tableWidth, rowHeight, 'F');
    }

    // Draw cell borders
    doc?.setDrawColor(...borderColor);
    currentX = startX;
    row?.forEach((cell, colIndex) => {
      doc?.rect(startX + colWidths?.slice(0, colIndex)?.reduce((a, b) => a + b, 0), currentY, colWidths?.[colIndex], rowHeight);
    });

    // Draw cell text
    currentX = startX;
    row?.forEach((cell, i) => {
      const cellText = String(cell || '');
      const maxWidth = colWidths?.[i] - 4;
      const lines = doc?.splitTextToSize(cellText, maxWidth);
      
      // Only show first line if text is too long
      const displayText = lines?.[0] || '';
      doc?.text(displayText, currentX + 2, currentY + 5.5);
      currentX += colWidths?.[i];
    });
    
    currentY += rowHeight;
  });

  return currentY;
};

/**
 * Generate PDF report for offer comparison
 * @param {Object} options - Report generation options
 * @param {Object} options.property - Property information
 * @param {Array} options.selectedOffers - Array of selected offers
 * @param {boolean} options.includePropertyDetails - Include property details section
 * @param {boolean} options.includeAgentNotes - Include agent notes in comparison
 * @param {boolean} options.aiEnabled - Include AI analysis
 * @param {Object} options.aiAnalysis - AI analysis data
 * @returns {void} - Downloads the PDF file
 */
export const generateOfferComparisonPDF = ({
  property,
  selectedOffers,
  includePropertyDetails,
  includeAgentNotes,
  aiEnabled,
  aiAnalysis,
  agentNameDisplay = 'full',
  buyerNameDisplay = 'full',
  contractUrls = {}
}) => {
  try {
    // Create new PDF document
    const doc = new jsPDF();
    let yPosition = 20;

    // Add header
    doc?.setFontSize(20);
    doc?.setTextColor(37, 99, 235);
    doc?.text('Offer Comparison Report', 20, yPosition);
    yPosition += 10;

    // Add generation date
    doc?.setFontSize(10);
    doc?.setTextColor(100, 100, 100);
    const generatedDate = new Date()?.toLocaleDateString('en-US', { 
      month: 'long', 
      day: 'numeric', 
      year: 'numeric' 
    });
    doc?.text(`Generated on ${generatedDate}`, 20, yPosition);
    yPosition += 15;

    // Add property information section
    if (includePropertyDetails && property) {
      doc?.setFontSize(14);
      doc?.setTextColor(0, 0, 0);
      doc?.text('Property Information', 20, yPosition);
      yPosition += 8;

      doc?.setFontSize(10);
      doc?.setTextColor(60, 60, 60);
      doc?.text(`Address: ${property?.address || 'N/A'}`, 25, yPosition);
      yPosition += 6;
      doc?.text(`Owner(s): ${property?.ownerNames || 'N/A'}`, 25, yPosition);
      yPosition += 6;
      if (property?.listingPrice) {
        doc?.text(`Listing Price: ${formatCurrency(property?.listingPrice)}`, 25, yPosition);
        yPosition += 6;
      }
      yPosition += 5;
    }

    // Add AI summary section
    if (aiEnabled && aiAnalysis?.summary) {
      doc?.setFontSize(14);
      doc?.setTextColor(0, 0, 0);
      doc?.text('AI-Generated Summary', 20, yPosition);
      yPosition += 8;

      doc?.setFontSize(9);
      doc?.setTextColor(60, 60, 60);
      const summaryLines = doc?.splitTextToSize(aiAnalysis?.summary, 170);
      doc?.text(summaryLines, 25, yPosition);
      yPosition += (summaryLines?.length * 5) + 8;
    }

    // Check if we need a new page
    if (yPosition > 250) {
      doc?.addPage();
      yPosition = 20;
    }

    // Add offer comparison table
    doc?.setFontSize(14);
    doc?.setTextColor(0, 0, 0);
    doc?.text('Offer Comparison', 20, yPosition);
    yPosition += 8;

    // Prepare table data
    const tableHeaders = ['Details', ...selectedOffers?.map((_, index) => `Offer ${index + 1}`)];
    const tableData = [
      ['Agent', ...selectedOffers?.map(o => formatNameByPrivacy(o?.buyerAgent, agentNameDisplay))],
      ['Buyer Name', ...selectedOffers?.map(o => formatNameByPrivacy(o?.buyerName, buyerNameDisplay))],
      ['Offer Price', ...selectedOffers?.map(o => formatCurrency(o?.offerPrice || 0))],
      ['Closing Date', ...selectedOffers?.map(o => formatDate(o?.closingDate))],
      ['Financing Type', ...selectedOffers?.map(o => o?.financingType || 'N/A')],
      ['Total Deposit', ...selectedOffers?.map(o => {
        const totalDeposit = (parseFloat(o?.downPayment) || 0) + (parseFloat(o?.earnestMoney) || 0);
        const percentage = o?.offerPrice ? ((totalDeposit / parseFloat(o?.offerPrice)) * 100)?.toFixed(2) : 0;
        return `${formatCurrency(totalDeposit)} (${percentage}%)`;
      })],
      ['Mortgage amount', ...selectedOffers?.map(o => {
        const totalDeposit = (parseFloat(o?.downPayment) || 0) + (parseFloat(o?.earnestMoney) || 0);
        const offerPrice = parseFloat(o?.offerPrice) || 0;
        const mortgageAmount = offerPrice - totalDeposit;
        const percentage = offerPrice ? ((mortgageAmount / offerPrice) * 100)?.toFixed(2) : 0;
        return `${formatCurrency(mortgageAmount)} (${percentage}%)`;
      })],
      ['Amount to Seller', ...selectedOffers?.map(o => {
        const offerPrice = parseFloat(o?.offerPrice) || 0;
        const propertyCommission = parseFloat(property?.commissionPercentage) || 0;
        const buyersAgentCommission = parseFloat(o?.buyersAgentCommission) || 0;

        // Calculate commission amounts in dollars
        const listingCommissionAmount = offerPrice * (propertyCommission / 100);
        const buyersAgentCommissionAmount = offerPrice * (buyersAgentCommission / 100);
        const totalCommissions = listingCommissionAmount + buyersAgentCommissionAmount;

        // Subtract total commissions from offer price
        const amountToSeller = offerPrice - totalCommissions;
        
        // Build breakdown text
        let breakdownText = formatCurrency(amountToSeller);
        if (propertyCommission > 0 || buyersAgentCommission > 0) {
          breakdownText += '\n';
          if (propertyCommission > 0) {
            breakdownText += `Listing: -${formatCurrency(listingCommissionAmount)} (${propertyCommission}%)`;
          }
          if (buyersAgentCommission > 0) {
            if (propertyCommission > 0) breakdownText += '\n';
            breakdownText += `Buyer's Agent: -${formatCurrency(buyersAgentCommissionAmount)} (${buyersAgentCommission}%)`;
          }
        }
        
        return breakdownText;
      })],
      ['Lender', ...selectedOffers?.map(o => o?.lender || 'N/A')],
      ['Contingencies', ...selectedOffers?.map(o => 
        o?.contingencies?.length > 0 ? o?.contingencies?.join(', ') : 'None'
      )],
    ];

    // Add agent notes row if enabled
    if (includeAgentNotes) {
      tableData?.push(['Provisions', ...selectedOffers?.map(o => o?.agentNotes || 'No provisions')]);
    }

    // Calculate column widths based on number of offers
    const numColumns = tableHeaders?.length;
    const pageWidth = doc?.internal?.pageSize?.width;
    const tableWidth = pageWidth - 40;
    const firstColWidth = 50;
    const remainingWidth = tableWidth - firstColWidth;
    const offerColWidth = remainingWidth / (numColumns - 1);
    const columnWidths = [firstColWidth, ...Array(numColumns - 1)?.fill(offerColWidth)];

    // Draw the table
    yPosition = drawTable(doc, tableHeaders, tableData, yPosition, {
      columnWidths,
      headerColor: [37, 99, 235],
      headerTextColor: [255, 255, 255],
      rowHeight: 8,
      headerHeight: 10,
      fontSize: 8,
      headerFontSize: 9
    });

    yPosition += 10;

    // Add contract URLs as clickable links
    const offersWithContracts = selectedOffers?.filter(o => contractUrls?.[o?.id]);
    if (offersWithContracts?.length > 0) {
      // Check if we need a new page
      if (yPosition > 250) {
        doc?.addPage();
        yPosition = 20;
      }

      doc?.setFontSize(12);
      doc?.setTextColor(0, 0, 0);
      doc?.text('Contract Documents', 20, yPosition);
      yPosition += 8;

      doc?.setFontSize(9);
      doc?.setTextColor(60, 60, 60);
      
      offersWithContracts?.forEach((offer, index) => {
        const offerIndex = selectedOffers?.findIndex(o => o?.id === offer?.id) + 1;
        const contractUrl = contractUrls?.[offer?.id];
        
        // Add offer label
        doc?.setTextColor(0, 0, 0);
        doc?.text(`Offer ${offerIndex} - ${offer?.buyerName}:`, 25, yPosition);
        yPosition += 5;
        
        // Add clickable link
        doc?.setTextColor(37, 99, 235);
        doc?.textWithLink('Click here to view contract', 25, yPosition, { url: contractUrl });
        doc?.setTextColor(100, 100, 100);
        yPosition += 4;
        
        // Add expiration notice
        doc?.setFontSize(7);
        doc?.text('(Link expires 1 week from report generation)', 25, yPosition);
        doc?.setFontSize(9);
        yPosition += 7;

        // Check if we need a new page
        if (yPosition > 270) {
          doc?.addPage();
          yPosition = 20;
        }
      });
      
      yPosition += 5;
    }

    // Add AI key differences section
    if (aiEnabled && aiAnalysis?.keyDifferences && aiAnalysis?.keyDifferences?.length > 0) {
      // Check if we need a new page
      if (yPosition > 250) {
        doc?.addPage();
        yPosition = 20;
      }

      doc?.setFontSize(14);
      doc?.setTextColor(0, 0, 0);
      doc?.text('Key Differences', 20, yPosition);
      yPosition += 8;

      doc?.setFontSize(9);
      doc?.setTextColor(60, 60, 60);
      aiAnalysis?.keyDifferences?.forEach((difference) => {
        const diffLines = doc?.splitTextToSize(`• ${difference}`, 165);
        doc?.text(diffLines, 25, yPosition);
        yPosition += (diffLines?.length * 5) + 2;

        // Check if we need a new page
        if (yPosition > 270) {
          doc?.addPage();
          yPosition = 20;
        }
      });
      yPosition += 5;
    }

    // Add deal risk assessment section
    if (aiEnabled && aiAnalysis?.dealRisks && aiAnalysis?.dealRisks?.length > 0) {
      // Check if we need a new page
      if (yPosition > 240) {
        doc?.addPage();
        yPosition = 20;
      }

      doc?.setFontSize(14);
      doc?.setTextColor(0, 0, 0);
      doc?.text('Deal Risk Assessment', 20, yPosition);
      yPosition += 8;

      aiAnalysis?.dealRisks?.forEach((risk) => {
        doc?.setFontSize(10);
        doc?.setTextColor(0, 0, 0);
        doc?.text(`Offer ${risk?.offerIndex + 1}: ${risk?.buyerName}`, 25, yPosition);
        yPosition += 6;

        doc?.setFontSize(9);
        doc?.setTextColor(60, 60, 60);
        doc?.text(`Risk Level: ${risk?.riskLevel || 'N/A'}`, 30, yPosition);
        yPosition += 5;

        if (risk?.risks && risk?.risks?.length > 0) {
          risk?.risks?.forEach((riskItem) => {
            const riskLines = doc?.splitTextToSize(`• ${riskItem}`, 160);
            doc?.text(riskLines, 30, yPosition);
            yPosition += (riskLines?.length * 5) + 1;
          });
        }
        yPosition += 5;

        // Check if we need a new page
        if (yPosition > 270) {
          doc?.addPage();
          yPosition = 20;
        }
      });
    }

    // Add financing concerns section
    if (aiEnabled && aiAnalysis?.financingConcerns && aiAnalysis?.financingConcerns?.length > 0) {
      // Check if we need a new page
      if (yPosition > 240) {
        doc?.addPage();
        yPosition = 20;
      }

      doc?.setFontSize(14);
      doc?.setTextColor(0, 0, 0);
      doc?.text('Financing Assessment', 20, yPosition);
      yPosition += 8;

      aiAnalysis?.financingConcerns?.forEach((concern) => {
        doc?.setFontSize(10);
        doc?.setTextColor(0, 0, 0);
        doc?.text(`Offer ${concern?.offerIndex + 1}: ${concern?.buyerName}`, 25, yPosition);
        yPosition += 6;

        doc?.setFontSize(9);
        doc?.setTextColor(60, 60, 60);
        doc?.text(`Strength: ${concern?.strength || 'N/A'}`, 30, yPosition);
        yPosition += 5;

        if (concern?.concerns && concern?.concerns?.length > 0) {
          concern?.concerns?.forEach((concernItem) => {
            const concernLines = doc?.splitTextToSize(`• ${concernItem}`, 160);
            doc?.text(concernLines, 30, yPosition);
            yPosition += (concernLines?.length * 5) + 1;
          });
        }
        yPosition += 5;

        // Check if we need a new page
        if (yPosition > 270) {
          doc?.addPage();
          yPosition = 20;
        }
      });
    }

    // Add recommendations section
    if (aiEnabled && aiAnalysis?.recommendations && aiAnalysis?.recommendations?.length > 0) {
      // Check if we need a new page
      if (yPosition > 240) {
        doc?.addPage();
        yPosition = 20;
      }

      doc?.setFontSize(14);
      doc?.setTextColor(0, 0, 0);
      doc?.text('Recommendations', 20, yPosition);
      yPosition += 8;

      doc?.setFontSize(9);
      doc?.setTextColor(60, 60, 60);
      aiAnalysis?.recommendations?.forEach((recommendation) => {
        const recLines = doc?.splitTextToSize(`• ${recommendation}`, 165);
        doc?.text(recLines, 25, yPosition);
        yPosition += (recLines?.length * 5) + 2;

        // Check if we need a new page
        if (yPosition > 270) {
          doc?.addPage();
          yPosition = 20;
        }
      });
    }

    // Add footer on last page
    const pageCount = doc?.internal?.getNumberOfPages();
    for (let i = 1; i <= pageCount; i++) {
      doc?.setPage(i);
      doc?.setFontSize(8);
      doc?.setTextColor(150, 150, 150);
      doc?.text(
        `Page ${i} of ${pageCount}`,
        doc?.internal?.pageSize?.width / 2,
        doc?.internal?.pageSize?.height - 10,
        { align: 'center' }
      );
    }

    // Generate filename
    const filename = `offer-comparison-${property?.address?.replace(/[^a-z0-9]/gi, '-')?.toLowerCase() || 'report'}-${Date.now()}.pdf`;
    
    // Save the PDF
    doc?.save(filename);
  } catch (error) {
    console.error('PDF Generation Error:', error);
    throw new Error(`Failed to generate PDF: ${error.message}`);
  }
};
